import { billingPrice, type BillingTerm } from "~/components/billing-toggle";

export type TechnicalComparisonRow = {
  label: string;
  values: string[];
};

type ComparisonGuide = {
  href: string;
  label: string;
};

export function PlanComparison({
  productName,
  plans,
  rows,
  guide,
  billingTerm = "monthly",
  note,
}: {
  productName: string;
  plans: { name: string; price: string }[];
  rows: TechnicalComparisonRow[];
  guide?: ComparisonGuide;
  billingTerm?: BillingTerm;
  note?: string;
}) {
  if (!rows.length) return null;

  return (
    <section className="border-y border-ink/10 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="eyebrow text-moss">Everything compared</p>
            <h2 className="display mt-3 text-4xl font-bold leading-[.95]">
              The details, clearly lined up.
            </h2>
          </div>
          <p className="max-w-md text-sm leading-6 text-ink/60">
            A straight comparison of what changes between {productName} plans.
            Application support is included across every plan.
          </p>
        </div>
        <div className="mt-9 overflow-x-auto rounded-3xl border border-ink/10 bg-white">
          <table className="w-full min-w-[44rem] border-collapse text-left text-sm">
            <thead className="bg-cream">
              <tr>
                <th className="w-[28%] px-6 py-5 font-bold">
                  Technical detail
                </th>
                {plans.map((plan) => (
                  <th key={plan.name} className="px-6 py-5">
                    <span className="block whitespace-nowrap font-bold">
                      {plan.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-ink/10 bg-cream/60">
                <th scope="row" className="px-6 py-4 font-bold">
                  Price for selected term
                </th>
                {plans.map((plan) => (
                  <td
                    key={plan.name}
                    className="whitespace-nowrap px-6 py-4 font-bold text-moss"
                  >
                    {billingPrice(plan.price, billingTerm)}
                  </td>
                ))}
              </tr>
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-ink/10">
                  <th scope="row" className="px-6 py-4 font-bold">
                    {row.label}
                  </th>
                  {plans.map((plan, index) => (
                    <td
                      key={plan.name}
                      className="whitespace-nowrap px-6 py-4 text-ink/65"
                    >
                      {row.values[index] ?? "Included"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {note && <p className="mt-4 text-xs leading-5 text-ink/60">{note}</p>}
        {guide && (
          <a
            href={guide.href}
            className="mt-7 inline-flex items-center gap-2 rounded-full border border-moss bg-white px-5 py-3 text-sm font-bold text-ink transition hover:-translate-y-0.5 hover:bg-leaf"
          >
            {guide.label} <span aria-hidden="true">→</span>
          </a>
        )}
      </div>
    </section>
  );
}
